import { Schema } from "effect";

export const AuthorizationErrorCode = /* @__PURE__ */ (() => Schema.Literals(["authentication-required", "invalid-assertion", "invalid-assertion-issuer", "invalid-assertion-audience", "invalid-assertion-signature", "invalid-assertion-time", "invalid-assertion-subject", "bootstrap-denied", "bootstrap-already-claimed", "local-resolver-forbidden", "invalid-origin", "invalid-csrf", "invalid-session", "revoked-session", "revoked-grant", "authorization-denied"]))();
export type AuthorizationErrorCode = typeof AuthorizationErrorCode.Type;

const AuthorizationErrorFields = /* @__PURE__ */ (() => Schema.Struct({
  code: AuthorizationErrorCode,
  message: Schema.String,
  status: Schema.Literals([401, 403, 409])
}))();
type AuthorizationErrorProperties = typeof AuthorizationErrorFields.Type;
export class AuthorizationError extends Schema.TaggedError<AuthorizationError>()(
  "AuthorizationError", AuthorizationErrorFields
) {
  constructor(code: AuthorizationErrorCode, message: string, status?: 401 | 403 | 409);
  constructor(properties: AuthorizationErrorProperties, options?: Schema.MakeOptions);
  constructor(code: AuthorizationErrorCode | AuthorizationErrorProperties, message?: string | Schema.MakeOptions, status?: 401 | 403 | 409) {
    super(typeof code === "string" ? {
      code,
      message: message as string,
      status: status ?? (code === "authentication-required" ? 401 : 403)
    } : code, typeof message === "object" ? message : undefined);
  }
}

export const StableIdentityText = (maximumLength = 256) => Schema.String.check(Schema.makeFilter(value =>
  value.length > 0 && value.length <= maximumLength && value === value.trim() &&
  !/[\u0000-\u001f\u007f]/.test(value)
));
export const PlatformAssertionKeyRequest = /* @__PURE__ */ (() => Schema.Struct({
  algorithm: Schema.Literal("RS256").pipe(Schema.mutableKey),
  keyId: StableIdentityText(512).pipe(Schema.optional, Schema.mutableKey),
}))();
export type PlatformAssertionKeyRequest = typeof PlatformAssertionKeyRequest.Type;

export interface PlatformAssertionValidationConfig {
  issuer: string;
  audience: string;
  resolveKey(
    request: PlatformAssertionKeyRequest
  ): Promise<CryptoKey | null> | CryptoKey | null;
  allowedAlgorithms?: readonly "RS256"[];
  groupsClaim?: string;
  clockSkewSeconds?: number;
  maxTokenLifetimeSeconds?: number;
  maxAssertionBytes?: number;
}

export const LocalOwnerResolverConfig = /* @__PURE__ */ (() => Schema.Struct({
  deploymentEnvironment: Schema.Literals(["production", "development", "test"]).pipe(Schema.mutableKey),
  subject: Schema.String.pipe(Schema.mutableKey),
  policySource: Schema.String.pipe(Schema.mutableKey),
  groups: Schema.Array(Schema.String).pipe(Schema.optional, Schema.mutableKey),
}))();
export type LocalOwnerResolverConfig = typeof LocalOwnerResolverConfig.Type;


export const DEFAULT_PLATFORM_ASSERTION_HEADER = "cf-access-jwt-assertion";

export const OpaqueSessionToken = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]{43}$/)))();

export const STATION_CSRF_HEADER = "x-workerstation-csrf";

export const STATION_SESSION_COOKIE = "__Host-workerstation-session";

export const StationRole = /* @__PURE__ */ (() => Schema.Literals(["reader", "runner", "reviewer", "maintainer", "owner"]))();
export type StationRole = typeof StationRole.Type;

export const SessionTransport = /* @__PURE__ */ (() => Schema.Literals(["browser", "mcp"]))();
export type SessionTransport = typeof SessionTransport.Type;

export const DeploymentBootstrapPolicy = /* @__PURE__ */ (() => Schema.Struct({
  policySource: Schema.String.pipe(Schema.mutableKey),
  allowedSubjects: Schema.Array(Schema.String).pipe(Schema.optional, Schema.mutableKey),
  allowedGroups: Schema.Array(Schema.String).pipe(Schema.optional, Schema.mutableKey),
}))();
export type DeploymentBootstrapPolicy = typeof DeploymentBootstrapPolicy.Type;

export const RoleGrant = /* @__PURE__ */ (() => Schema.Struct({
  stationId: Schema.String.pipe(Schema.mutableKey),
  principalId: Schema.String.pipe(Schema.mutableKey),
  role: StationRole.pipe(Schema.mutableKey),
  revision: Schema.Number.pipe(Schema.mutableKey),
  grantedBy: Schema.String.pipe(Schema.mutableKey),
  grantedAt: Schema.Number.pipe(Schema.mutableKey),
  revokedBy: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  revokedReason: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  revokedAt: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
}))();
export type RoleGrant = typeof RoleGrant.Type;

export const StoredStationSession = /* @__PURE__ */ (() => Schema.Struct({
  stationId: Schema.String.pipe(Schema.mutableKey),
  sessionHash: Schema.String.pipe(Schema.mutableKey),
  principalId: Schema.String.pipe(Schema.mutableKey),
  csrfHash: Schema.Union([Schema.String, Schema.Null]).pipe(Schema.mutableKey),
  transport: SessionTransport.pipe(Schema.mutableKey),
  platformIssuer: Schema.String.pipe(Schema.mutableKey),
  platformSubject: Schema.String.pipe(Schema.mutableKey),
  policySource: Schema.String.pipe(Schema.mutableKey),
  grantRevisionAtIssue: Schema.Number.pipe(Schema.mutableKey),
  createdAt: Schema.Number.pipe(Schema.mutableKey),
  expiresAt: Schema.Number.pipe(Schema.mutableKey),
  lastUsedAt: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  rotatedFromHash: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  rotatedAt: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  revokedAt: Schema.Number.pipe(Schema.optional, Schema.mutableKey),
  revokedReason: Schema.String.pipe(Schema.optional, Schema.mutableKey),
}))();
export type StoredStationSession = typeof StoredStationSession.Type;

export const InitialOwnerClaim = /* @__PURE__ */ (() => Schema.Struct({
  stationId: Schema.String.pipe(Schema.mutableKey),
  principalId: Schema.String.pipe(Schema.mutableKey),
  platformIssuer: Schema.String.pipe(Schema.mutableKey),
  platformSubject: Schema.String.pipe(Schema.mutableKey),
  policySource: Schema.String.pipe(Schema.mutableKey),
  claimedAt: Schema.Number.pipe(Schema.mutableKey),
}))();
export type InitialOwnerClaim = typeof InitialOwnerClaim.Type;

export const RoleChangeRequest = /* @__PURE__ */ (() => Schema.Struct({
  stationId: Schema.String.pipe(Schema.mutableKey),
  actorPrincipalId: Schema.String.pipe(Schema.mutableKey),
  actorGrantRevision: Schema.Number.pipe(Schema.mutableKey),
  targetPrincipalId: Schema.String.pipe(Schema.mutableKey),
  role: Schema.Union([StationRole, Schema.Null]).pipe(Schema.mutableKey),
  reason: Schema.String.pipe(Schema.mutableKey),
  changedAt: Schema.Number.pipe(Schema.mutableKey),
}))();
export type RoleChangeRequest = typeof RoleChangeRequest.Type;

export const OwnerRecoveryRequest = /* @__PURE__ */ (() => Schema.Struct({
  stationId: Schema.String.pipe(Schema.mutableKey),
  replacementPrincipalId: Schema.String.pipe(Schema.mutableKey),
  platformIssuer: Schema.String.pipe(Schema.mutableKey),
  platformSubject: Schema.String.pipe(Schema.mutableKey),
  policySource: Schema.String.pipe(Schema.mutableKey),
  reason: Schema.String.pipe(Schema.mutableKey),
  compromisedPrincipalIds: Schema.Array(Schema.String).pipe(Schema.mutableKey),
  compromisedSessionHashes: Schema.Array(Schema.String).pipe(Schema.mutableKey),
  recoveredAt: Schema.Number.pipe(Schema.mutableKey),
}))();
export type OwnerRecoveryRequest = typeof OwnerRecoveryRequest.Type;

export const OwnerRecoveryResult = /* @__PURE__ */ (() => Schema.Struct({
  recoveryEpoch: Schema.Number.pipe(Schema.mutableKey),
  ownerGrant: RoleGrant.pipe(Schema.mutableKey),
}))();
export type OwnerRecoveryResult = typeof OwnerRecoveryResult.Type;

export const AuthenticatedStationPrincipal = /* @__PURE__ */ (() => Schema.Struct({
  stationId: Schema.String.pipe(Schema.mutableKey),
  principalId: Schema.String.pipe(Schema.mutableKey),
  platformIssuer: Schema.String.pipe(Schema.mutableKey),
  platformSubject: Schema.String.pipe(Schema.mutableKey),
  role: StationRole.pipe(Schema.mutableKey),
  grantRevision: Schema.Number.pipe(Schema.mutableKey),
  sessionHash: Schema.String.pipe(Schema.mutableKey),
  transport: SessionTransport.pipe(Schema.mutableKey),
}))();
export type AuthenticatedStationPrincipal = typeof AuthenticatedStationPrincipal.Type;

export const ReviewDisposition = /* @__PURE__ */ (() => Schema.Literals(["address", "resolve", "dismiss", "reopen"]))();
export type ReviewDisposition = typeof ReviewDisposition.Type;

export const StationCapability = /* @__PURE__ */ (() => Schema.Union([Schema.Struct({
  kind: Schema.Literal("source.read").pipe(Schema.mutableKey),
}), Schema.Struct({
  kind: Schema.Literal("development.manage").pipe(Schema.mutableKey),
}), Schema.Struct({
  kind: Schema.Literal("worktree.mutate").pipe(Schema.mutableKey),
  assignedPrincipalId: Schema.String.pipe(Schema.mutableKey),
}), Schema.Struct({
  kind: Schema.Literal("lease.reassign").pipe(Schema.mutableKey),
}), Schema.Struct({
  kind: Schema.Literal("run.cancel").pipe(Schema.mutableKey),
  assignedPrincipalId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
}), Schema.Struct({
  kind: Schema.Literal("review.disposition").pipe(Schema.mutableKey),
  disposition: ReviewDisposition.pipe(Schema.mutableKey),
  blocking: Schema.Boolean.pipe(Schema.mutableKey),
  severity: Schema.Literals(["info", "warning", "error", "critical"]).pipe(Schema.mutableKey),
  category: Schema.String.pipe(Schema.mutableKey),
  authorPrincipalId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  assignedRunnerPrincipalId: Schema.String.pipe(Schema.optional, Schema.mutableKey),
}), Schema.Struct({
  kind: Schema.Literal("safety.override").pipe(Schema.mutableKey),
}), Schema.Struct({
  kind: Schema.Literal("target.publish").pipe(Schema.mutableKey),
}), Schema.Struct({
  kind: Schema.Literal("roles.manage").pipe(Schema.mutableKey),
})]))();
export type StationCapability = typeof StationCapability.Type;

export const AuthorizationDecision = /* @__PURE__ */ (() => Schema.Struct({
  allowed: Schema.Boolean.pipe(Schema.mutableKey),
  reason: Schema.Literals(["allowed", "authentication-required", "station-mismatch", "scope-mismatch", "insufficient-role", "requires-two-principal-approval", "non-overridable", "unknown-capability"]).pipe(Schema.mutableKey),
}))();
export type AuthorizationDecision = typeof AuthorizationDecision.Type;

export const GateCondition = /* @__PURE__ */ (() => Schema.Literals(["required-check", "required-review-coverage", "critical-finding", "security-finding", "source-conflict", "stale-target", "isolation-failure", "invalid-authorization", "publication-cas-failure"]))();
export type GateCondition = typeof GateCondition.Type;

export const RecordedSafetyApproval = /* @__PURE__ */ (() => Schema.Struct({
  principalId: Schema.String.pipe(Schema.mutableKey),
  grantRevision: Schema.Number.pipe(Schema.mutableKey),
}))();
export type RecordedSafetyApproval = typeof RecordedSafetyApproval.Type;

export const IssuedStationSession = /* @__PURE__ */ (() => Schema.Struct({
  token: OpaqueSessionToken.pipe(Schema.mutableKey),
  csrfToken: OpaqueSessionToken.pipe(Schema.optional, Schema.mutableKey),
  setCookie: Schema.String.pipe(Schema.optional, Schema.mutableKey),
  expiresAt: Schema.Number.pipe(Schema.mutableKey),
}))();
export type IssuedStationSession = typeof IssuedStationSession.Type;

export const IssuedBrowserStationSession = /* @__PURE__ */ (() => Schema.Struct({
  ...IssuedStationSession.fields,
  csrfToken: OpaqueSessionToken.pipe(Schema.mutableKey),
  setCookie: Schema.String.pipe(Schema.mutableKey),
}))();
export type IssuedBrowserStationSession = typeof IssuedBrowserStationSession.Type;

/** Native actor port retained until Phase 6. Mutations recheck authority transactionally. */
export interface StationAuthorizationStore {
  claimInitialOwner(input: InitialOwnerClaim): Promise<RoleGrant | null>;
  readCurrentGrant(
    stationId: string,
    principalId: string
  ): Promise<RoleGrant | null>;
  changeRole(input: RoleChangeRequest): Promise<RoleGrant>;
  readSession(
    stationId: string,
    sessionHash: string
  ): Promise<StoredStationSession | null>;
  rotateSession(
    stationId: string,
    previousSessionHash: string | null,
    session: StoredStationSession
  ): Promise<void>;
  revokeSession(
    stationId: string,
    sessionHash: string,
    principalId: string,
    reason: string,
    revokedAt: number
  ): Promise<void>;
  recoverOwner(input: OwnerRecoveryRequest): Promise<OwnerRecoveryResult>;
}
